import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, ChevronRight, Sun, Snowflake, Leaf, Flower2 } from 'lucide-react';
import { useState } from 'react';
import OceanBackground from '@/components/background/OceanBackground';

const hotDestinations = [
  { name: '大理', city: '云南', score: 9.4, img: 'dali' },
  { name: '成都', city: '四川', score: 9.2, img: 'chengdu' },
  { name: '厦门', city: '福建', score: 9.0, img: 'xiamen' },
  { name: '西安', city: '陕西', score: 9.3, img: 'xian' },
];

const allDestinations = [
  { name: '丽江古城', city: '云南·丽江', tags: ['古镇', '浪漫'], season: '春', desc: '纳西文化发源地，小桥流水人家', img: 'lijiang' },
  { name: '稻城亚丁', city: '四川·甘孜', tags: ['自然', '高原'], season: '秋', desc: '蓝色星球上的最后一片净土', img: 'daocheng' },
  { name: '青岛', city: '山东', tags: ['海岛', '美食'], season: '夏', desc: '红瓦绿树，碧海蓝天', img: 'qingdao' },
];

const seasons = [
  { label: '春', icon: Flower2 },
  { label: '夏', icon: Sun },
  { label: '秋', icon: Leaf },
  { label: '冬', icon: Snowflake },
];

const tags = ['热门', '小众', '海岛', '古镇', '美食', '人文'];

const Destinations = () => {
  const navigate = useNavigate();
  const [activeTag, setActiveTag] = useState('全部');
  const [activeSeason, setActiveSeason] = useState(null);

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-20 relative z-10">
        <div className="bg-gradient-to-r from-primary to-accent rounded-card p-8 text-white mb-8 shadow-card">
          <h1 className="text-3xl font-bold mb-4">发现你的下一站</h1>
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
            <Input placeholder="搜索目的地..." className="pl-12 h-12 rounded-btn bg-white text-text-primary border-0" />
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-lg font-bold text-text-primary mb-4">热门目的地</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {hotDestinations.map((d) => (
              <Card key={d.name} className="min-w-[280px] h-[180px] border-0 shadow-card rounded-card bg-white overflow-hidden relative cursor-pointer" onClick={() => navigate(`/destination/${d.name}`)}>
                <img src={`https://picsum.photos/seed/${d.img}/600/400`} alt="" className="w-full h-full mx-auto object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-lg font-bold">{d.name}</p>
                  <p className="text-xs opacity-80">推荐指数 {d.score}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Badge onClick={() => setActiveTag('全部')} className={`cursor-pointer rounded-full ${activeTag === '全部' ? 'bg-primary text-white' : 'bg-white text-text-secondary'}`}>全部</Badge>
          {tags.map((t) => <Badge key={t} onClick={() => setActiveTag(t)} className={`cursor-pointer rounded-full ${activeTag === t ? 'bg-primary text-white' : 'bg-white text-text-secondary'}`}>{t}</Badge>)}
        </div>
        <div className="flex gap-2 mb-6">
          {seasons.map((s) => (
            <button key={s.label} onClick={() => setActiveSeason(s.label === activeSeason ? null : s.label)} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs ${activeSeason === s.label ? 'bg-primary text-white' : 'bg-white text-text-secondary'}`}>
              <s.icon className="w-3 h-3" /> {s.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allDestinations.map((d) => (
            <Card key={d.name} className="border-0 shadow-card rounded-card bg-white overflow-hidden hover:shadow-hover transition-all cursor-pointer" onClick={() => navigate(`/destination/${d.name}`)}>
              <div className="aspect-[3/4] bg-bg-secondary relative">
                <img src={`https://picsum.photos/seed/${d.img}/500/667`} alt="" className="w-full h-full mx-auto object-cover" />
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-text-primary mb-1">{d.name}</h3>
                <p className="text-xs text-text-tertiary mb-2">{d.city}</p>
                <div className="flex flex-wrap gap-1 mb-2">
                  {d.tags.map((t) => <Badge key={t} className="bg-primary-light text-primary rounded-full text-[10px]">{t}</Badge>)}
                </div>
                <p className="text-xs text-text-secondary line-clamp-2 mb-2">{d.desc}</p>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="bg-accent/20 text-secondary rounded-full text-[10px]">{d.season}季最佳</Badge>
                  <span className="text-xs text-primary flex items-center gap-0.5">详情 <ChevronRight className="w-3 h-3" /></span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Destinations;
